/**
 * FieldMapping presentation rules (Register Import Wizard, AVA-602 slice 1b.2): which blockers,
 * alerts and Facility-column states the Map Fields step shows. Pure functions so the wording and
 * the blocking rules are testable without mounting Vuetify.
 *
 * A blocker is { key, kind: 'err' | 'warn' | 'info', icon, title, text, foot?, register?, footOnly?, section? }:
 *   - kind 'err' blocks the primary action; its `foot` (or title) is repeated next to it;
 *   - `register` marks a register-level problem (never hidden by the saved-schema banner);
 *   - `footOnly` is shown next to the primary action only;
 *   - `section` names the collapsible Map Fields section the error belongs to (MAP_SECTION).
 */
import { REGISTER_KIND, resultingMode } from '../../../modules/registerImportWizardModel.js';

const fmt = (value) => Number(value || 0).toLocaleString('en-US');

/**
 * Collapsible Map Fields sections (all open by default). A blocker that belongs to a section carries
 * `section`, so a NEW blocking error can re-open it and its alert can offer "Show".
 */
export const MAP_SECTION = Object.freeze({
  SETTINGS: 'settings',
  FACILITY: 'facility',
  COLUMNS: 'columns',
});

/**
 * The Facility column cell (Settings) and its rail summary value, per Register kind. Compact layout:
 * `cap` is one short line under the control (null = none); `tip` is the full explanation behind the
 * info icon next to the label.
 */
export function facilityColumnPresentation(kind, { hasFacilityColumn, facilityName, groupName } = {}) {
  const off = (cap, tip) => ({
    enabled: false,
    badge: { text: 'Not used', cls: 'riw-badge--opt' },
    cap: cap ? { icon: 'mdi-information-outline', text: cap, required: false } : null,
    tip,
    stat: { value: 'Not used', tone: 'muted' },
  });
  switch (kind) {
    case REGISTER_KIND.GROUP_SHARED:
      return off(
        'Shared register — not needed',
        `Shared registers don’t use a Facility column. Rows are saved at Facility Group level and every Facility in ${groupName || 'the Facility Group'} sees them.`,
      );
    case REGISTER_KIND.GROUP_UNSET:
      return off('Locked until the mode is set', 'Unavailable until an administrator sets the register mode (Shared or Separated).');
    case REGISTER_KIND.GROUP_SEPARATED:
      return {
        enabled: true,
        badge: { text: 'Required', cls: 'riw-badge--req' },
        cap: hasFacilityColumn ? null : { icon: 'mdi-alert-circle-outline', text: 'Required for Separated', required: true },
        tip: 'Separated register: pick the column that names each row’s Facility. Names are matched to the Facility Group’s Facilities; rows with a blank or unknown Facility are skipped.',
        stat: hasFacilityColumn ? null : { value: 'Required', tone: 'wn' },
      };
    case REGISTER_KIND.GROUP_EMPTY: {
      const mode = resultingMode(kind, hasFacilityColumn);
      return {
        enabled: true,
        badge: {
          text: mode,
          cls: 'riw-badge--mode',
          icon: mode === 'Separated' ? 'mdi-call-split' : 'mdi-share-variant-outline',
          title: `This first import makes the register ${mode}`,
        },
        cap: null,
        tip: 'This first import sets the register mode: with a Facility column it becomes Separated, without one it becomes Shared. The mode can’t be changed afterwards.',
        stat: hasFacilityColumn ? null : { value: 'Not set', tone: 'muted' },
      };
    }
    case REGISTER_KIND.FACILITY:
      return off(
        'Facility register — not needed',
        `Rows always belong to ${facilityName || 'this register’s Facility'}. A Facility register ignores the Facility column.`,
      );
    default:
      // Schema-update mode: the Settings section is not shown.
      return off('', '');
  }
}

/** Sections that currently have a blocking error (order-stable, no duplicates). */
export function errorSections(blockers) {
  const sections = (blockers || []).filter((b) => b.kind === 'err' && b.section).map((b) => b.section);
  return [...new Set(sections)];
}

/** Sections whose blocking error is NEW since the last check — these open by themselves, once. */
export function newlyFailingSections(previous, current) {
  const before = new Set(previous || []);
  return (current || []).filter((section) => !before.has(section));
}

export function mappingBlockers(state) {
  if (state.schemaUpdateMode) return [];
  const list = [];

  if (state.registerKind === REGISTER_KIND.GROUP_UNSET) {
    // An API source can still be saved as Manual Only (its pulls are refused until the mode is set),
    // so there the problem is shown but does not block the mapping step (`advisory`).
    const isApi = !!state.sourceLabel && state.sourceLabel !== 'Excel';
    list.push({
      key: 'unset',
      kind: 'err',
      register: true,
      advisory: isApi,
      icon: 'mdi-lock-alert-outline',
      title: 'Register mode not set.',
      text: isApi
        ? `This Facility Group register already has ${fmt(state.registerRowCount)} rows but no Shared or Separated mode. You can save this source as Manual Only; its pulls are refused until an administrator sets the mode.`
        : `This Facility Group register already has ${fmt(state.registerRowCount)} rows but no Shared/Separated mode. An administrator must set its mode before importing.`,
      foot: 'An administrator must set this register’s mode',
    });
  }
  if (state.facilityColumnMissing) {
    list.push({
      key: 'facilityColumn',
      kind: 'err',
      section: MAP_SECTION.SETTINGS,
      icon: 'mdi-domain-off',
      title: 'No Facility column selected.',
      text: 'Separated registers need the column that names each row’s Facility.',
    });
  }
  if (state.facilityApplies && state.membersLoading) {
    list.push({ key: 'members', kind: 'info', blocking: true, icon: 'mdi-progress-clock', title: 'Loading the Facility Group’s Facilities…', text: '', foot: 'Loading Facilities' });
  } else if (state.facilityApplies && state.membersError) {
    list.push({ key: 'members', kind: 'err', icon: 'mdi-alert-circle-outline', title: 'Facilities could not be loaded.', text: state.membersError, foot: 'Facilities could not be loaded' });
  } else if (state.facilityApplies && !state.facilityAliasesValid) {
    list.push({
      key: 'aliases',
      kind: 'err',
      section: MAP_SECTION.FACILITY,
      icon: 'mdi-alert-circle-outline',
      title: 'Facility name mapping has an error.',
      text: 'Fix the highlighted row in Facility name mapping to continue.',
      foot: 'Fix the Facility name mapping',
    });
  } else if (!state.facilityMappingReady && !state.facilityColumnMissing && state.registerKind !== REGISTER_KIND.GROUP_UNSET) {
    list.push({
      key: 'facility',
      kind: 'err',
      icon: 'mdi-alert-circle-outline',
      title: 'Facility assignment configuration is incomplete.',
      text: 'Select the required Facility column or correct the Facility name mapping.',
    });
  }
  if (state.mappedColumnsCount === 0) {
    list.push({
      key: 'noFields',
      kind: 'err',
      section: MAP_SECTION.COLUMNS,
      icon: 'mdi-alert-circle-outline',
      title: 'No field names configured yet.',
      text: `Configure field names for your ${state.sourceLabel || 'Excel'} columns to continue.`,
    });
  }
  // `identityGuidance`: the host (API wizard) shows its own schedule-aware identity callout instead.
  if (state.mappedColumnsCount > 0 && !state.hasIdentity && !state.identityGuidance) {
    list.push({
      key: 'noIdentity',
      kind: 'warn',
      icon: 'mdi-fingerprint-off',
      title: 'No identity column selected.',
      text: 'Without an identity column, re-importing this data adds new copies of the rows instead of updating them.',
    });
  }
  const skipped = state.counts ? state.counts.skipped : 0;
  if (skipped && !list.some((b) => b.kind === 'err')) {
    list.push({
      key: 'skips',
      kind: 'warn',
      footOnly: true,
      icon: 'mdi-alert-outline',
      title: `${fmt(skipped)} ${skipped === 1 ? 'row' : 'rows'} will be skipped — see Preview`,
      text: '',
    });
  }
  return list;
}

/** True when a blocker stops the primary action (an `advisory` error is shown but doesn't block). */
export function isBlocking(blocker) {
  return (blocker.kind === 'err' && !blocker.advisory) || blocker.blocking === true;
}

/**
 * A saved mapping loaded into a Shared register loses its Facility column (and aliases) instead of
 * being refused as a whole; this note says what was dropped and what was kept.
 * @param {object} state - { column: string ('' = aliases only), kept: number, isExcelSource: boolean }
 */
export function facilityDroppedAlert({ column, kept, isExcelSource }) {
  const subject = isExcelSource ? 'This mapping' : 'This source';
  const what = column
    ? `${subject} used “${column}” as its Facility column. Shared registers don’t use one, so it was dropped`
    : `${subject} had Facility name mappings. Shared registers don’t use them, so they were dropped`;
  const keptText = `${fmt(kept)} field ${kept === 1 ? 'mapping was' : 'mappings were'} kept`;
  return {
    key: 'facilityDropped',
    kind: 'warn',
    role: 'status',
    icon: 'mdi-domain-remove',
    title: column ? 'Facility column removed from the saved mapping.' : 'Facility name mappings removed from the saved mapping.',
    text: `${what}; the other ${keptText}.${isExcelSource ? '' : ' Saving updates the source.'}`,
  };
}

/** The single hint repeated next to the primary action: the first blocking one, else a warning. */
export function footerHint(blockers) {
  const list = blockers || [];
  const blocking = list.find(isBlocking);
  if (blocking) return { kind: blocking.kind === 'err' ? 'err' : 'info', icon: blocking.icon, text: (blocking.foot || blocking.title).replace(/\.$/, '') };
  const warn = list.find((b) => b.kind === 'warn');
  return warn ? { kind: 'warn', icon: 'mdi-alert-outline', text: warn.title.replace(/\.$/, '') } : null;
}

/** The compact alerts above the mapping table (saved-schema banner, mode notes, blockers). */
export function mappingAlerts(state) {
  const list = [];
  if (state.showSavedBanner) {
    list.push({
      key: 'saved',
      kind: 'ok',
      icon: 'mdi-table-check',
      title: `Saved schema for “${state.registerName}” available`,
      text: '— pre-fills names, types, mandatory fields and identity.',
      actions: true,
      oneLine: true,
    });
  }
  if (state.schemaUpdateMode) {
    list.push({
      key: 'schema',
      kind: 'info',
      icon: 'mdi-table-cog',
      title: 'Schema Update Mode.',
      text: `You can rename fields and change link designations for “${state.registerName}”. Field types and structure are locked because the register already holds data.`,
    });
    return list;
  }
  const subject = state.isExcelSource ? 'This import makes' : 'The first accepted run makes';
  if (state.registerKind === REGISTER_KIND.GROUP_EMPTY) {
    list.push(state.hasFacilityColumn
      ? { key: 'mode', kind: 'info', icon: 'mdi-call-split', title: `${subject} the register Separated.`, text: 'Each Facility sees only its rows; rows with a blank or unknown Facility are skipped. Fixed after this import.' }
      : { key: 'mode', kind: 'info', icon: 'mdi-share-variant-outline', title: `${subject} the register Shared.`, text: 'Every Facility in the Facility Group sees all rows. Choose a Facility column to make it Separated instead. Fixed after this import.' });
  } else if (state.registerKind === REGISTER_KIND.GROUP_SHARED) {
    list.push({
      key: 'mode',
      kind: 'info',
      icon: 'mdi-share-variant-outline',
      title: 'Shared register.',
      text: `Rows are saved at Facility Group level; every Facility in ${state.groupName || 'the Facility Group'} sees all rows.`,
    });
  }
  // `droppedFacilityColumn`: null = nothing dropped; '' = only Facility name mappings were dropped.
  if (state.droppedFacilityColumn !== null && state.droppedFacilityColumn !== undefined) {
    list.push(facilityDroppedAlert({
      column: state.droppedFacilityColumn,
      kept: state.keptFieldCount || 0,
      isExcelSource: state.isExcelSource,
    }));
  }
  // While the saved-schema banner shows, mapping errors wait (they are still in the footer hint);
  // register-level problems always show.
  const shown = (state.blockers || [])
    .filter((b) => !b.footOnly)
    .filter((b) => !state.showSavedBanner || b.kind !== 'err' || b.register);
  return [...list, ...shown];
}
